import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrialStatus } from '@/context/TrialContext';
import { useAuth } from '@/App';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  Zap, 
  AlertTriangle, 
  X,
  CheckCircle,
  MessageCircle,
  Bot,
  Package,
  BarChart3,
  Lock
} from 'lucide-react';

/**
 * TrialExpiryPopup - Modal popup yang muncul ketika trial tersisa <= 3 hari
 * 
 * Behavior:
 * - Muncul otomatis saat user login dan trial tersisa <= 3 hari
 * - Bisa di-dismiss, tidak muncul lagi dalam 24 jam (localStorage)
 * - Menampilkan countdown dan benefit upgrade
 */
const TrialExpiryPopup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isTrialUser, isTrialExpired, trialExpiresAt } = useTrialStatus();
  const [isOpen, setIsOpen] = useState(false);

  const getTimeLeft = () => {
    if (!trialExpiresAt) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const difference = new Date(trialExpiresAt).getTime() - Date.now();
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  };

  // Dihitung langsung dari trialExpiresAt supaya judul dan countdown tidak pernah
  // menampilkan angka hari yang berbeda (dulu judul memakai pembulatan ke atas).
  const [timeLeft, setTimeLeft] = useState(getTimeLeft);

  // Check if popup should be shown
  useEffect(() => {
    if (!user || !isTrialUser || isTrialExpired || !trialExpiresAt) {
      return;
    }

    // Check if user dismissed popup in last 24 hours
    const dismissedAt = localStorage.getItem('trial_popup_dismissed');
    if (dismissedAt) {
      const dismissedTime = new Date(dismissedAt).getTime();
      const now = new Date().getTime();
      const hoursSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60);
      
      // Don't show if dismissed less than 24 hours ago
      if (hoursSinceDismissed < 24) {
        return;
      }
    }

    // Muncul kalau sisa trial 3 hari atau kurang. Memakai pembulatan ke BAWAH,
    // sumber angka yang sama dengan judul & countdown (dulu pakai Math.ceil dari
    // TrialContext sehingga sisa 3 hari 1 jam malah tidak pernah memunculkan popup).
    const daysLeftFloor = Math.floor(
      (new Date(trialExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    if (daysLeftFloor <= 3) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, isTrialUser, isTrialExpired, trialExpiresAt]);

  // Countdown timer
  useEffect(() => {
    if (!trialExpiresAt || !isOpen) return;

    setTimeLeft(getTimeLeft());
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [trialExpiresAt, isOpen]);

  const handleDismiss = () => {
    localStorage.setItem('trial_popup_dismissed', new Date().toISOString());
    setIsOpen(false);
  };

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate('/payment?plan=starter');
  };

  const padZero = (num) => String(num).padStart(2, '0');

  // Judul, tingkat urgensi, dan countdown WAJIB memakai sumber angka yang sama
  const displayDays = timeLeft.days;

  // Get urgency level for styling
  const getUrgencyLevel = () => {
    if (displayDays === 0) return 'critical'; // Less than 24 hours
    if (displayDays === 1) return 'high';
    return 'medium';
  };

  const urgency = getUrgencyLevel();
  const urgencyColors = {
    critical: {
      bg: 'bg-red-50 dark:bg-red-950/40',
      border: 'border-red-200 dark:border-red-900/60',
      text: 'text-red-600 dark:text-red-300',
      accent: 'bg-red-600',
      button: 'bg-red-600 hover:bg-red-700'
    },
    high: {
      bg: 'bg-orange-50 dark:bg-orange-950/40',
      border: 'border-orange-200 dark:border-orange-900/60',
      text: 'text-orange-600 dark:text-orange-300',
      accent: 'bg-orange-500',
      button: 'bg-orange-500 hover:bg-orange-600'
    },
    medium: {
      bg: 'bg-amber-50 dark:bg-amber-950/40',
      border: 'border-amber-200 dark:border-amber-900/60',
      text: 'text-amber-600 dark:text-amber-300',
      accent: 'bg-amber-500',
      button: 'bg-amber-500 hover:bg-amber-600'
    }
  };

  const colors = urgencyColors[urgency];

  const benefits = [
    { icon: Bot, text: 'AI Chatbot aktif 24/7' },
    { icon: MessageCircle, text: 'Balas pesan tanpa batas' },
    { icon: Package, text: 'Kelola produk unlimited' },
    { icon: BarChart3, text: 'Analitik bisnis lengkap' }
  ];

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="sm:max-w-[480px] p-0 max-h-[92vh] overflow-y-auto [&>button]:hidden"
        data-testid="trial-expiry-popup"
      >
        {/* Header with urgency color */}
        <div className={`${colors.accent} text-white p-4 sm:p-6 relative`}>
          {/* Single close button in header */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full transition-colors z-10 dark:bg-black/25"
            data-testid="trial-popup-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-4 pr-10">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 dark:bg-black/25">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <DialogTitle
                className="text-base sm:text-xl font-bold leading-snug text-white"
                data-testid="trial-popup-title"
              >
                {displayDays === 0 ? 'Trial Berakhir Hari Ini!' : `Trial Berakhir ${displayDays} Hari Lagi`}
              </DialogTitle>
              <DialogDescription className="text-white/80 text-xs sm:text-sm">
                Jangan sampai bisnis Anda terhenti
              </DialogDescription>
            </div>
          </div>

          {/* Countdown Timer — grid 4 kolom supaya tidak melebar keluar layar HP */}
          <div className="grid grid-cols-4 gap-1.5 sm:gap-3" data-testid="trial-popup-countdown">
            {[
              { value: timeLeft.days, label: 'Hari' },
              { value: timeLeft.hours, label: 'Jam' },
              { value: timeLeft.minutes, label: 'Menit' },
              { value: timeLeft.seconds, label: 'Detik' }
            ].map((unit) => (
              <div key={unit.label} className="text-center min-w-0">
                <div className="bg-white/20 rounded-lg px-1 py-2 dark:bg-black/25">
                  <span className="text-lg sm:text-2xl font-bold font-mono tabular-nums">
                    {padZero(unit.value)}
                  </span>
                </div>
                <span className="text-[10px] sm:text-xs text-white/70 mt-1 block truncate">
                  {unit.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className={`${colors.bg} ${colors.border} border rounded-xl p-4 mb-5`}>
            <p className={`${colors.text} text-sm font-medium mb-1`}>
              {displayDays === 0 
                ? <><AlertTriangle className="w-4 h-4 inline mr-1" />Setelah trial berakhir, akun Anda akan menjadi Read-Only</>
                : 'Upgrade sekarang sebelum trial berakhir'
              }
            </p>
            <p className="text-slate-600 dark:text-zinc-300 text-xs">
              AI Chatbot, kirim pesan, dan tambah produk akan dinonaktifkan.
            </p>
          </div>

          <h3 className="font-semibold text-slate-800 mb-3 dark:text-zinc-50">
            Dengan upgrade, Anda mendapatkan:
          </h3>
          
          <div className="space-y-2.5 mb-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 dark:bg-indigo-950/40">
                    <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="text-sm text-slate-700 dark:text-zinc-200">{benefit.text}</span>
                  <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                </div>
              );
            })}
          </div>

          {/* CTA Buttons */}
          <div className="space-y-2">
            <Button 
              onClick={handleUpgrade}
              className={`w-full ${colors.button} text-white font-semibold h-auto py-3.5 whitespace-normal leading-snug`}
              data-testid="trial-popup-upgrade-btn"
            >
              <Zap className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm sm:text-base">Upgrade Sekarang — Mulai Rp 199.000/bulan</span>
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleDismiss}
              className="w-full text-slate-500 hover:text-slate-700 dark:text-zinc-200"
              data-testid="trial-popup-remind-later-btn"
            >
              Ingatkan saya nanti
            </Button>
          </div>

          {/* Trust badge */}
          <p className="text-center text-xs text-slate-500 mt-4 dark:text-zinc-400">
            <Lock className="w-3 h-3 inline mr-1" />Pembayaran aman • Batalkan kapan saja
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrialExpiryPopup;
